/**
 * Progress Dashboard Component
 * Displays detailed learning analytics and progress tracking
 */

import React, { useState, useEffect } from 'react';
import {
  Clock,
  Award,
  BarChart3,
  Trophy,
  Flame,
  BookOpen,
  CheckCircle,
  Star,
  Activity,
} from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { useAuth } from '../../contexts/AuthContext';
import { progressService } from '../../services/firebase/progress';
import { courseService } from '../../services/firebase/courses';
import { Course, UserProgress } from '../../types/course';

/**
 * Learning statistics interface
 */
interface LearningStats {
  totalCoursesEnrolled: number;
  totalCoursesCompleted: number;
  totalTimeSpent: number;
  totalQuizzesTaken: number;
  averageQuizScore: number;
  currentStreak: number;
  longestStreak: number;
}

/**
 * Progress dashboard component
 */
export const ProgressDashboard: React.FC = () => {
  const { user } = useAuth();
  const [learningStats, setLearningStats] = useState<LearningStats | null>(null);
  const [enrolledCourses, setEnrolledCourses] = useState<Course[]>([]);
  const [courseProgress, setCourseProgress] = useState<{ [courseId: string]: UserProgress }>({});
  const [loading, setLoading] = useState(true);

  /**
   * Load progress data
   */
  useEffect(() => {
    const loadProgressData = async () => {
      if (!user) return;

      try {
        setLoading(true);

        // Load learning statistics
        const stats = await progressService.getUserLearningStats();
        setLearningStats(stats);

        // Load enrolled courses
        const { courses } = await courseService.getCourses();
        const enrolled: Course[] = [];
        const progress: { [courseId: string]: UserProgress } = {};

        for (const course of courses) {
          const userProgress = await courseService.getUserProgress(course.id);
          if (userProgress) {
            enrolled.push(course);
            progress[course.id] = userProgress;
          }
        }

        setEnrolledCourses(enrolled);
        setCourseProgress(progress);
      } catch (error) {
        console.error('Error loading progress data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProgressData();
  }, [user]);

  /**
   * Format duration
   */
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  /**
   * Get completion status color
   */
  const getCompletionColor = (percentage: number) => {
    if (percentage >= 100) return 'text-green-600';
    if (percentage >= 70) return 'text-blue-600';
    if (percentage >= 30) return 'text-yellow-600';
    return 'text-gray-600';
  };

  /**
   * Get streak emoji
   */
  const getStreakEmoji = (streak: number) => {
    if (streak >= 30) return '🔥';
    if (streak >= 14) return '⚡';
    if (streak >= 7) return '🌟';
    if (streak >= 3) return '💪';
    return '🎯';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!learningStats) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Learning Data Yet</h3>
        <p className="text-gray-600 mb-6">Start taking courses to see your progress analytics here.</p>
        <Button variant="primary" onClick={() => window.location.href = '/courses'}>
          Browse Courses
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Learning Progress</h1>
        <p className="text-gray-600">Track your learning journey and achievements</p>
      </div>

      {/* Key Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-1">
            {learningStats.totalCoursesEnrolled}
          </h3>
          <p className="text-gray-600 text-sm">Courses Enrolled</p>
        </Card>

        <Card className="p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-1">
            {learningStats.totalCoursesCompleted}
          </h3>
          <p className="text-gray-600 text-sm">Courses Completed</p>
        </Card>

        <Card className="p-6 text-center">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-1">
            {formatDuration(learningStats.totalTimeSpent)}
          </h3>
          <p className="text-gray-600 text-sm">Time Spent Learning</p>
        </Card>

        <Card className="p-6 text-center">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Flame className="w-6 h-6 text-orange-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-1 flex items-center justify-center">
            {learningStats.currentStreak}
            <span className="ml-2 text-xl">{getStreakEmoji(learningStats.currentStreak)}</span>
          </h3>
          <p className="text-gray-600 text-sm">Day Streak</p>
        </Card>
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quiz Performance */}
        <Card className="p-6">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
              <Star className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Quiz Performance</h3>
              <p className="text-gray-600 text-sm">Your assessment results</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Quizzes Taken</span>
              <span className="font-semibold text-gray-800">{learningStats.totalQuizzesTaken}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Score</span>
              <span className="font-semibold text-gray-800">
                {learningStats.averageQuizScore.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-yellow-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(learningStats.averageQuizScore, 100)}%` }}
              ></div>
            </div>
          </div>
        </Card>

        {/* Learning Streaks */}
        <Card className="p-6">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4">
              <Activity className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Learning Streaks</h3>
              <p className="text-gray-600 text-sm">Your consistency tracking</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Current Streak</span>
              <span className="font-semibold text-gray-800 flex items-center">
                {learningStats.currentStreak} days
                <span className="ml-2">{getStreakEmoji(learningStats.currentStreak)}</span>
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Longest Streak</span>
              <span className="font-semibold text-gray-800 flex items-center">
                {learningStats.longestStreak} days
                <Trophy className="w-4 h-4 ml-2 text-yellow-500" />
              </span>
            </div>
            <div className="text-center p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Keep it up!</p>
              <p className="text-xs text-gray-500">
                {learningStats.currentStreak === 0 
                  ? "Start learning today to begin your streak"
                  : `${7 - (learningStats.currentStreak % 7)} more days to reach the next milestone`
                }
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Course Progress */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Course Progress</h3>
              <p className="text-gray-600 text-sm">Your enrolled courses</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/courses'}>
            Browse More
          </Button>
        </div>

        {enrolledCourses.length === 0 ? (
          <div className="text-center py-8">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">You haven't enrolled in any courses yet.</p>
            <Button variant="primary" onClick={() => window.location.href = '/courses'}>
              Explore Courses
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {enrolledCourses.map(course => {
              const progress = courseProgress[course.id];
              if (!progress) return null;

              return (
                <div key={course.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 mb-1">{course.title}</h4>
                      <p className="text-sm text-gray-600">{course.category}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${getCompletionColor(progress.completionPercentage)}`}>
                        {Math.round(progress.completionPercentage)}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {progress.completedModules} / {progress.totalModules} modules
                      </p>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress.completionPercentage}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>Time spent: {formatDuration(progress.totalTimeSpent)}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = `/courses/${course.id}`}
                    >
                      {progress.completionPercentage > 0 ? 'Continue' : 'Start'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Achievements Section */}
      <Card className="p-6">
        <div className="flex items-center mb-6">
          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
            <Award className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Achievements</h3>
            <p className="text-gray-600 text-sm">Your learning milestones</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* First Course Achievement */}
          <div className={`p-4 rounded-lg border-2 ${
            learningStats.totalCoursesEnrolled >= 1 
              ? 'border-green-200 bg-green-50' 
              : 'border-gray-200 bg-gray-50'
          }`}>
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                learningStats.totalCoursesEnrolled >= 1 
                  ? 'bg-green-100 text-green-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}>
                <BookOpen className="w-6 h-6" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-1">First Steps</h4>
              <p className="text-xs text-gray-600">Enroll in your first course</p>
              {learningStats.totalCoursesEnrolled >= 1 && (
                <div className="mt-2">
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    Achieved! 🎉
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Course Completion Achievement */}
          <div className={`p-4 rounded-lg border-2 ${
            learningStats.totalCoursesCompleted >= 1 
              ? 'border-blue-200 bg-blue-50' 
              : 'border-gray-200 bg-gray-50'
          }`}>
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                learningStats.totalCoursesCompleted >= 1 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}>
                <CheckCircle className="w-6 h-6" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-1">Finisher</h4>
              <p className="text-xs text-gray-600">Complete your first course</p>
              {learningStats.totalCoursesCompleted >= 1 && (
                <div className="mt-2">
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    Achieved! 🏆
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Streak Achievement */}
          <div className={`p-4 rounded-lg border-2 ${
            learningStats.currentStreak >= 7 
              ? 'border-orange-200 bg-orange-50' 
              : 'border-gray-200 bg-gray-50'
          }`}>
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                learningStats.currentStreak >= 7 
                  ? 'bg-orange-100 text-orange-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}>
                <Flame className="w-6 h-6" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-1">On Fire</h4>
              <p className="text-xs text-gray-600">7-day learning streak</p>
              {learningStats.currentStreak >= 7 ? (
                <div className="mt-2">
                  <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                    Achieved! 🔥
                  </span>
                </div>
              ) : (
                <div className="mt-2">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {7 - learningStats.currentStreak} days to go
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

